import type { HelpContentMap } from './types';
import { brandConfig } from '../brands';

/**
 * デフォルトのヘルプ文言（全ブランド共通ベース）
 * OEM で差し替えたい項目は各ブランドファイルで部分上書きする。
 * brandConfig は VITE_BRAND に応じてモジュールロード時に決定するため、
 * ここで読むと OEM 毎に URL 等が自動で差し替わる。
 */
export const defaultHelpContent: HelpContentMap = {
  aspectRatio: (
    <>
      <p>
        アスペクト比は画像の<strong>形（縦横の比率）</strong>を決める設定です。掲載先や用途に合わせて選ぶと、トリミングされずに綺麗に表示できます。
      </p>
      <ul>
        <li>
          <strong>16:9</strong> — 横長。YouTubeサムネイル、Web広告バナー、PCモニター向け
        </li>
        <li>
          <strong>9:16</strong> — 縦長。Instagram/Facebookストーリーズ、TikTok、YouTubeショート向け
        </li>
        <li>
          <strong>1:1</strong> — 正方形。Instagram通常投稿、プロフィール画像など
        </li>
        <li>
          <strong>指定なし</strong> — AIに比率を委ねます。迷ったらこちら
        </li>
      </ul>
      <p>
        右の「その他」から、対応していない比率も選べます。<code>*</code> が付いた比率は生成AIが厳密に再現できないため、最も近い比率で出力されます。
      </p>
      <p>
        <small>
          ※ 実際のピクセルサイズは「解像度」と組み合わせて決まります（選んだ比率を保ったまま、長辺が解像度で指定した大きさになります）。
        </small>
      </p>
    </>
  ),
  resolution: (
    <>
      <p>
        解像度は画像の<strong>大きさ・きめ細かさ</strong>を決める設定です。<strong>1K・2K・4K の数字は「長い方の辺のおおよそのピクセル数」</strong>を表し、数値が大きいほど精細になりますが、生成時間とポイント消費が増えます。
      </p>
      <ul>
        <li>
          <strong>1K</strong>（長辺 約1280px）— SNS投稿やWeb表示向け。バランス型
        </li>
        <li>
          <strong>2K</strong>（長辺 約1920px）— 標準。広告バナーや商品画像など多くの用途に十分（デフォルト）
        </li>
        <li>
          <strong>4K</strong>（長辺 約3840px）— 高精細。大型ディスプレイや印刷物、部分トリミングを前提にする場合に
        </li>
      </ul>
      <p>
        <small>
          ※ 上の px はこのアプリでの長辺の目安です。「1K/2K/4K」はおおまかな呼称で、厳密な定義（例: 1K = 1024px）とは少しずれる場合があります。<br />
          ※ 動画の「720p / 1080p」は<strong>縦の高さ</strong>を表す別の指標です。静止画の 1K/2K/4K は<strong>長辺</strong>が基準のため、数字どうしがそのまま対応するわけではありません。
        </small>
      </p>
    </>
  ),
  duration: (
    <>
      <p>
        動画全体の<strong>想定尺（おおよその長さ）</strong>です。AI がこの尺に収まるようシーン数や各カットの長さを構成します。
      </p>
      <p>
        一覧に無い長さにしたいときは一番下の「カスタム」を選び、秒数（4〜300秒の整数）を入力してください。
      </p>
    </>
  ),
  outputFormat: (
    <>
      <p>
        生成画像のファイル形式です。用途に合わせて選んでください。
      </p>
      <ul>
        <li>
          <strong>指定なし</strong> — モデルの既定形式で出力します。迷ったらこちら
        </li>
        <li>
          <strong>PNG</strong> — 可逆圧縮で画質劣化なし。背景透過に対応。ファイルサイズは大きめ
        </li>
        <li>
          <strong>JPEG</strong> — 写真向け。ファイルサイズが小さく Web 配信向き。透過は不可
        </li>
        <li>
          <strong>WebP</strong> — 最新フォーマット。同等画質で PNG/JPEG より軽量。透過にも対応
        </li>
      </ul>
      <p>
        モデルが対応していない形式はグレーアウトされます。Web 広告やバナー等で軽さ重視なら <strong>JPEG</strong> / <strong>WebP</strong>、印刷や透過素材として使うなら <strong>PNG</strong> がおすすめです。
      </p>
    </>
  ),
  promptImage: (
    <>
      <p>
        プロンプトは、生成したい画像の内容を<strong>自然文で AI に伝える指示文</strong>です。具体的に書くほど意図どおりの画像になりやすくなります。
      </p>
      <p>コツ:</p>
      <ul>
        <li>
          <strong>主題を明確に</strong> — 「誰／何が」「どこで」「何をしているか」
        </li>
        <li>
          <strong>構図・アングル</strong> — 「俯瞰」「正面」「斜め45度」など
        </li>
        <li>
          <strong>雰囲気・スタイル</strong> — 「高級感」「ナチュラル」「写真風」など
        </li>
        <li>
          <strong>ライティング</strong> — 「柔らかい自然光」「夕焼け」など
        </li>
      </ul>
      <p>
        例:「白背景に置かれたスニーカー。周囲に植物の葉を配置し、真上からの俯瞰アングル。ナチュラルで清潔感のある雰囲気。」
      </p>
      <p>
        日本語・英語どちらでも入力できます。文字数制限はありません。「履歴」ボタンから過去のプロンプトを呼び出して再利用・アレンジも可能です。
      </p>
    </>
  ),
  promptVideo: (
    <>
      <p>
        プロンプトは、生成したい動画の内容を<strong>自然文で AI に伝える指示文</strong>です。静止画と違い、<strong>動き・カメラワーク・時間経過</strong>まで含めて書けます。
      </p>
      <p>コツ:</p>
      <ul>
        <li>
          <strong>動きを明記</strong> — 「回転」「ズームイン」「パン」「徐々に近づく」など
        </li>
        <li>
          <strong>主題と背景</strong> — 「誰／何が」「どこで」「何をしているか」
        </li>
        <li>
          <strong>雰囲気・ライティング</strong> — 「自然光」「温かい」「クール」など
        </li>
        <li>
          <strong>時間経過</strong> — 「最初は〜、次に〜、最後に〜」と展開を書ける
        </li>
      </ul>
      <p>
        例:「白背景でスニーカーが回転しながら表示される。カメラがゆっくりズームインし、素材の質感を見せる。最後にブランドロゴが表示される。」
      </p>
      <p>
        構成を細かく調整したい場合は、シーンを分割して作れる<strong>絵コンテ作成モード</strong>もあります。
      </p>
    </>
  ),
  generateTextFromProduct: (
    <>
      <p>
        画像内に表示するテキストを、紐づけた<strong>商品の説明文から AI に作らせる</strong>ための指示スイッチです。
      </p>
      <p>
        <strong>生成する</strong>: 「画像内テキストは商品説明文をもとに作成してください」と AI に指示します。商品名・説明・特徴に沿った、商材ブレのないコピーが出やすくなります。
      </p>
      <p>
        <strong>指定なし</strong>: AI はプロンプトや他の入力欄（メインコピー・CTA 等）に従ってテキストを決めます。商品と切り離したキャンペーンコピーを使いたいときはこちら。
      </p>
      <p>
        <strong>生成しない</strong>: 商品情報などからテキストを自動生成しないよう AI に明示的に指示します。メインコピー・CTA 等で指定したテキスト以外の文字が画像に入らなくなります。
      </p>
      <p>
        ※ このスイッチに関係なく、紐づけた商品の情報（名前・説明・特徴など）は参考情報として常に AI に渡っています。ON／OFF は<strong>「画像内の文言に商品説明文を反映させるか」</strong>の違いです。
      </p>
      <p>
        ※ 「生成する」は素材に商品が紐づいている場合のみ選択できます。先に商品を紐づけてください（商品の代わりに説明つきのブランドが素材に紐づいている場合は、ブランド情報をもとにテキストを生成できます）。「指定なし」「生成しない」は紐づけがなくても選択できます。
      </p>
    </>
  ),
  mediaMenu: (
    <>
      <p>
        メニューは、広告プラットフォームの<strong>枠の種類</strong>を指します。選ぶと、その枠で一般的に使われる出力画像（サイズ・仕様）とアスペクト比の候補が自動で絞り込まれ、迷わず設定できます。
      </p>
      <ul>
        <li>
          <strong>メニュー</strong>: プラットフォーム内の広告タイプ（例: スポンサーブランド広告、DSP、検索連動など）
        </li>
        <li>
          <strong>出力画像</strong>: そのメニューで必要な具体的な画像仕様（例: 1200×628、300×250 など）
        </li>
      </ul>
      <p>
        仕様が変更されたり、新しくメニューが追加されたりして内容が変わっている場合は、
        <a href={brandConfig.urls.contact} target="_blank" rel="noopener noreferrer">こちらまで連絡</a>してください。
      </p>
    </>
  ),
  cta: (
    <>
      <p>
        CTA（Call To Action）は、画像を見た人に<strong>取ってほしい行動</strong>を促す短い文言です。広告バナーやLPのクリック率に直結する重要な要素です。
      </p>
      <p>例:</p>
      <ul>
        <li>「今すぐ購入」「無料で試す」「申し込み」</li>
        <li>「詳しくはこちら」「資料ダウンロード」</li>
        <li>「公式サイトへ」「お問い合わせ」</li>
      </ul>
      <p>コツ:</p>
      <ul>
        <li>
          <strong>短く・具体的な動詞</strong>で。受動的な表現より能動的に
        </li>
        <li>
          <strong>緊急性・限定感</strong>を添えるとクリック率が上がる（「期間限定」「本日まで」など）
        </li>
        <li>最大35文字。ボタン風に目立たせたいときは短いほど効果的</li>
      </ul>
    </>
  ),
};
